/**
 * Speaker Boundary Interference Response (SBIR) — closed-form quarter-wave
 * cancellation for shoebox rooms. Client-safe; no backend required.
 */

import { SPEED_OF_SOUND_MPS } from '@/lib/acousticsPhysics';

export type SbirWall = 'front' | 'rear' | 'left' | 'right';

export interface SbirNull {
  wall: SbirWall;
  distanceM: number;
  nullHz: number;
  /** Approximate notch depth in dB (negative). */
  depthDb: number;
}

export interface SbirInput {
  lengthM: number;
  widthM: number;
  /** Speaker X from left wall (m). */
  speakerXM: number;
  /** Speaker Y from front wall (m). */
  speakerYM: number;
}

/** Quarter-wave cancellation: f = c / (4 · d). */
export function quarterWaveNullHz(distanceM: number): number {
  if (distanceM <= 0) return Infinity;
  return SPEED_OF_SOUND_MPS / (4 * distanceM);
}

/**
 * Deeper notches when the boundary is closer (stronger path coherence).
 * Clamped to a marketing-realistic −6…−18 dB range.
 */
export function estimateNotchDepthDb(distanceM: number): number {
  const d = Math.max(0.15, distanceM);
  const depth = -6 - 8 / d;
  return Math.max(-18, Math.min(-6, Math.round(depth * 10) / 10));
}

export function calculateSbirNulls(input: SbirInput): SbirNull[] {
  const { lengthM, widthM, speakerXM, speakerYM } = input;
  const walls: Array<{ wall: SbirWall; distanceM: number }> = [
    { wall: 'front', distanceM: speakerYM },
    { wall: 'rear', distanceM: lengthM - speakerYM },
    { wall: 'left', distanceM: speakerXM },
    { wall: 'right', distanceM: widthM - speakerXM },
  ];

  return walls
    .filter((w) => w.distanceM > 0.05)
    .map((w) => {
      const nullHz = Math.round(quarterWaveNullHz(w.distanceM) * 10) / 10;
      return {
        wall: w.wall,
        distanceM: Math.round(w.distanceM * 100) / 100,
        nullHz,
        depthDb: estimateNotchDepthDb(w.distanceM),
      };
    })
    .sort((a, b) => a.nullHz - b.nullHz);
}

/** Deepest (most negative) null in the audible bass band. */
export function primarySbirNull(nulls: SbirNull[], maxHz = 250): SbirNull | null {
  const inBand = nulls.filter((n) => n.nullHz <= maxHz);
  if (inBand.length === 0) return null;
  return inBand.reduce((a, b) => (a.depthDb < b.depthDb ? a : b));
}

/**
 * Linear magnitude response with Lorentzian notches at each SBIR null.
 * Baseline = 1.0; notches multiply toward 10^(depthDb/20).
 */
export function sbirMagnitudeResponse(freqs: number[], nulls: SbirNull[]): number[] {
  return freqs.map((f) => {
    let mag = 1;
    for (const n of nulls) {
      if (!Number.isFinite(n.nullHz) || n.nullHz <= 0) continue;
      const bw = Math.max(8, n.nullHz * 0.12);
      const floor = 10 ** (n.depthDb / 20);
      const t = ((f - n.nullHz) / bw) ** 2;
      const notch = floor + (1 - floor) * (t / (1 + t));
      mag *= notch;
    }
    return mag;
  });
}

/** Log-spaced frequency grid for tool charts / auralization (modal band). */
export function toolFreqGrid(fMin = 20, fMax = 300, n = 160): number[] {
  const lo = Math.log10(fMin);
  const hi = Math.log10(fMax);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(10 ** (lo + ((hi - lo) * i) / (n - 1)));
  }
  return out;
}
